import { Card, CardContent } from "./card"
import AnimatedNumber from "./animated-number"
import LoadingSkeleton from "./loading-skeleton"
import { useLoadingState } from "./hooks/use-loading-state"
import {
    getSizeClasses,
    getNumberSizeClasses,
    getAlignClasses,
    getContainerWidthClasses,
    getResponsiveClasses,
    getGridClasses,
    getConnectedGridClasses,
    getContentPaddingClasses,
    getIconSizeClasses,
    getSpacingClasses,
} from "./card-styles"

/**
 * 單一統計卡片項目
 *
 * @param {object} props
 * @param {object} props.stat - 單筆統計資料
 * @param {boolean} [props.enableAnimation=true] - 是否啟用數字動畫
 * @param {number} [props.index=0] - 項目索引，用於動畫延遲
 * @param {boolean} [props.showContent=true] - 是否顯示內容
 * @param {"xs"|"sm"|"md"|"lg"|"xl"|"xxl"} [props.size="md"] - 尺寸
 * @param {"left"|"center"|"right"} [props.contentAlign="left"] - 文字對齊
 * @param {"single"|"horizontal"} [props.layout="horizontal"] - 版型
 * @param {string} [props.defaultBackgroundColor="bg-white"] - 預設背景顏色
 */
function StatDisplay({
    stat,
    enableAnimation = true,
    index = 0,
    showContent = true,
    size = "md",
    contentAlign = "left",
    layout = "horizontal",
    defaultBackgroundColor = "bg-white",
    compact = false,
}) {
    const getChangeColor = (type) => {
        switch (type) {
            case "increase":
                return "text-green-600"
            case "decrease":
                return "text-red-600"
            default:
                return "text-gray-600"
        }
    }

    const alignClasses = getAlignClasses(contentAlign)
    const effectiveSize = compact ? "sm" : size
    const numberSizeClasses = getNumberSizeClasses(effectiveSize, layout)
    const itemBackgroundColor = compact ? "bg-transparent" : stat?.backgroundColor || defaultBackgroundColor
    const contentPaddingClasses = compact ? "py-2 px-0" : getContentPaddingClasses(effectiveSize)
    const iconSizeClasses = getIconSizeClasses(effectiveSize)
    const spacingClasses = getSpacingClasses(effectiveSize)
    const containerShapeClasses = compact ? "rounded-none" : "rounded-lg"
    const labelTextClasses = compact ? "text-xs" : "text-sm"
    const changeTextClasses = compact ? "text-xs" : "text-sm"

    return (
        <div
            className={`flex transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"} ${itemBackgroundColor} ${containerShapeClasses} ${contentPaddingClasses} ${spacingClasses.gap} ${alignClasses}`}
        >
            {stat?.icon && stat?.iconPosition !== "right" && <div className={`text-gray-400 flex-shrink-0 ${iconSizeClasses}`}>{stat?.icon}</div>}

            <div className="flex flex-col flex-1 min-w-0">
                <div className={`font-bold ${numberSizeClasses} ${spacingClasses.mb}`} style={{ color: stat?.color }}>
                    <AnimatedNumber
                        value={stat?.value}
                        duration={stat?.animationDuration || 2000}
                        delay={stat?.animationDelay || index * 200}
                        enabled={enableAnimation && showContent}
                    />
                </div>
                <div className={`${labelTextClasses} text-gray-600 ${spacingClasses.mt} line-clamp-2 break-words`}>{stat?.label}</div>
                {stat?.change && (
                    <div className={`${changeTextClasses} ${spacingClasses.mt} ${getChangeColor(stat?.change.type)} truncate`}>
                        {stat?.change.value}
                    </div>
                )}
            </div>
            {stat?.icon && stat?.iconPosition === "right" && <div className={`text-gray-400 flex-shrink-0 ${iconSizeClasses}`}>{stat?.icon}</div>}

        </div>
    )
}

/**
 * 統計卡片群組元件
 *
 * @param {object} props
 * @param {"single"|"horizontal"} [props.layout] - 版型（單卡 or 多欄水平）
 * @param {Array} props.stats - 統計資料陣列
 * @param {string} [props.className=""] - 額外的 className
 * @param {boolean} [props.enableAnimation=true] - 是否啟用數字動畫
 * @param {boolean} [props.isLoading=false] - 是否顯示 loading 狀態
 * @param {number} [props.loadingDuration=1500] - loading 時間(ms)
 * @param {"xs"|"sm"|"md"|"lg"|"xl"|"xxl"} [props.size="md"] - 尺寸
 * @param {"left"|"center"|"right"} [props.contentAlign="left"] - 對齊方式
 * @param {string} [props.backgroundColor="bg-white"] - 背景顏色
 * @param {"separated"|"connected"} [props.connection="separated"] - 分隔模式
 * @param {number} [props.columnsPerRow=3] - 預設欄數
 * @param {object} [props.responsiveColumns] - 響應式欄數設定
 * @param {object} [props.responsiveSize] - 響應式尺寸設定
 */
export default function StatsCard({
    layout,
    stats,
    className = "",
    enableAnimation = true,
    isLoading = false,
    loadingDuration = 1500,
    size = "md",
    contentAlign = "left",
    backgroundColor = "bg-white",
    connection = "separated",
    columnsPerRow = 3,
    responsiveColumns,
    responsiveSize,
}) {
    const { internalLoading, showContent } = useLoadingState({ isLoading, loadingDuration })

    const sizeClasses = getSizeClasses(size)
    const alignClasses = getAlignClasses(contentAlign)
    const containerWidthClasses = getContainerWidthClasses(size, layout)
    const responsiveClasses = getResponsiveClasses(responsiveSize)
    const contentPaddingClasses = getContentPaddingClasses(size)
    const iconSizeClasses = getIconSizeClasses(size)
    const spacingClasses = getSpacingClasses(size)

    if (internalLoading) {
        return (
            <LoadingSkeleton
                layout={layout}
                statsCount={stats.length}
                className={className}
                size={size}
                contentAlign={contentAlign}
                backgroundColor={backgroundColor}
                connection={connection}
                columnsPerRow={columnsPerRow}
                responsiveColumns={responsiveColumns}
            />
        )
    }

    if (layout === "single") {
        const stat = stats[0]
        const cardBackgroundColor = stat?.backgroundColor || backgroundColor

        return (
            <Card className={`${containerWidthClasses} ${className} transition-all duration-300`}>
                <CardContent className={`${cardBackgroundColor} rounded-lg ${sizeClasses} ${responsiveClasses}`}>
                    <div className={`flex ${spacingClasses.gap} ${alignClasses}`}>
                        {stat?.icon && stat?.iconPosition !== "right" && (
                            <div
                                className={`text-gray-400 flex-shrink-0 transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"}`}
                            >
                                <div className={iconSizeClasses}>{stat?.icon}</div>
                            </div>
                        )}

                        <div className="flex flex-col flex-1 min-w-0">
                            <h3
                                className={`font-medium text-gray-900 ${spacingClasses.mb} transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"} line-clamp-2`}
                            >
                                {stat?.label}
                            </h3>
                            <div
                                className={`font-bold text-gray-900 ${spacingClasses.mb} transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"} ${getNumberSizeClasses(size, layout)}`}
                            >
                                <AnimatedNumber
                                    value={stat?.value}
                                    duration={stat?.animationDuration || 2500}
                                    delay={stat?.animationDelay || 300}
                                    enabled={enableAnimation && showContent}
                                />
                            </div>
                            {stat?.change && (
                                <div
                                    className={`text-sm transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"} ${stat?.change.type === "increase" ? "text-green-600" : "text-red-600"}`}
                                >
                                    {stat?.change.value}
                                </div>
                            )}
                        </div>
                        {stat?.icon && stat?.iconPosition === "right" && (
                            <div
                                className={`text-gray-400 flex-shrink-0 transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"}`}
                            >
                                <div className={iconSizeClasses}>{stat?.icon}</div>
                            </div>
                        )}

                    </div>
                </CardContent>
            </Card>
        )
    }

    if (connection === "connected") {
        const gridClasses = getConnectedGridClasses(columnsPerRow, responsiveColumns)

        return (
            <Card className={`w-full ${className} transition-all duration-300`}>
                <CardContent className={`${backgroundColor} rounded-lg ${sizeClasses} ${responsiveClasses} p-0`}>
                    {/* <div className={gridClasses}>
                        {stats.map((stat, index) => (
                            <div
                                key={index}
                                className={`${contentPaddingClasses} first:pt-${contentPaddingClasses.split("-")[1]} last:pb-${contentPaddingClasses.split("-")[1]}`}
                            >
                                <div
                                    className={`flex transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"} ${spacingClasses.gap} ${alignClasses}`}
                                >
                                    {stat?.icon && stat?.iconPosition !== "right" && <div className={`text-gray-400 flex-shrink-0 ${iconSizeClasses}`}>{stat?.icon}</div>}

                                    <div className="flex flex-col flex-1 min-w-0">
                                        <div
                                            className={`font-bold ${getNumberSizeClasses(size, layout)} ${spacingClasses.mb}`}
                                            style={{ color: stat?.color }}
                                        >
                                            <AnimatedNumber
                                                value={stat?.value}
                                                duration={stat?.animationDuration || 2000}
                                                delay={stat?.animationDelay || index * 200}
                                                enabled={enableAnimation && showContent}
                                            />
                                        </div>
                                        <div className={`text-sm text-gray-600 ${spacingClasses.mt}`}>{stat?.label}</div>
                                        {stat?.change && (
                                            <div
                                                className={`text-sm ${spacingClasses.mt} ${stat?.change.type === "increase" ? "text-green-600" : "text-red-600"}`}
                                            >
                                                {stat?.change.value}
                                            </div>
                                        )}
                                    </div>
                                    {stat?.icon && stat?.iconPosition === "right" && <div className={`text-gray-400 flex-shrink-0 ${iconSizeClasses}`}>{stat?.icon}</div>}

                                </div>
                            </div>
                        ))}
                    </div> */}
                    <div className={gridClasses}>
                        {stats.map((stat, index) => (
                            <div key={index} className={contentPaddingClasses}>
                                <div
                                    className={`
          flex flex-col sm:flex-row ${alignClasses} ${spacingClasses.gap}
          transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"}
        `}
                                >
                                    {/* 左側 icon（小螢幕在上方） */}
                                    {stat?.icon && stat?.iconPosition !== "right" && (
                                        <div className={`text-gray-400 flex-shrink-0 ${iconSizeClasses}`}>
                                            {stat?.icon}
                                        </div>
                                    )}

                                    {/* 文字區 */}
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <div
                                            className={`font-bold ${getNumberSizeClasses(size, layout)} ${spacingClasses.mb}`}
                                            style={{ color: stat?.color }}
                                        >
                                            <AnimatedNumber
                                                value={stat?.value}
                                                duration={stat?.animationDuration || 2000}
                                                delay={stat?.animationDelay || index * 200}
                                                enabled={enableAnimation && showContent}
                                            />
                                        </div>

                                        <div className={`text-xs sm:text-sm text-gray-600 ${spacingClasses.mt} line-clamp-2`}>
                                            {stat?.label}
                                        </div>

                                        {stat?.change && (
                                            <div
                                                className={`text-xs sm:text-sm ${spacingClasses.mt} ${stat?.change.type === "increase" ? "text-green-600" : "text-red-600"
                                                    }`}
                                            >
                                                {stat?.change.value}
                                            </div>
                                        )}
                                    </div>

                                    {/* 右側 icon（小螢幕在下方） */}
                                    {stat?.icon && stat?.iconPosition === "right" && (
                                        <div className={`text-gray-400 flex-shrink-0 ${iconSizeClasses}`}>
                                            {stat?.icon}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        )
    }

    const gridClasses = getGridClasses(columnsPerRow, responsiveColumns)

    return (
        <>
            {/* 手機版：純文字條列（保留最外層 Card） */}
            <div className={`w-full ${className} md:hidden`}>
                <Card className="transition-all duration-300">
                    <CardContent className="p-0">
                        <ul className="space-y-2 px-3 py-2">
                            {stats.map((stat, index) => (
                                <li key={index} className="flex items-start gap-2">
                                    <span
                                        className="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
                                        style={{ backgroundColor: stat?.color || "#6b7280" }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="text-xs text-gray-600 truncate max-w-[55%]">{stat?.label}</span>
                                            <span className="text-sm font-semibold truncate max-w-[45%]" style={{ color: stat?.color }}>
                                                <AnimatedNumber
                                                    value={stat?.value}
                                                    duration={stat?.animationDuration || 2000}
                                                    delay={stat?.animationDelay || index * 200}
                                                    enabled={enableAnimation && showContent}
                                                />
                                            </span>
                                        </div>
                                        {stat?.change && (
                                            <div className={`text-xs }`}>
                                                {stat?.change.value}
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            </div>

            {/* 桌面版：多卡格線 */}
            <div className={`w-full ${className} ${gridClasses} hidden md:grid`}>
                {stats.map((stat, index) => (
                    <Card key={index} className="transition-all duration-300" style={{ backgroundColor: stat.backgroundColor || backgroundColor }}>
                        <CardContent className={`${sizeClasses} ${responsiveClasses} p-0`} >
                            <StatDisplay
                                stat={stat}
                                enableAnimation={enableAnimation}
                                index={index}
                                showContent={showContent}
                                size={size}
                                contentAlign={contentAlign}
                                layout={layout}
                                defaultBackgroundColor={backgroundColor}
                            />
                        </CardContent>
                    </Card>
                ))}
            </div>
        </>
    )
}
